import { useEffect, useMemo, useRef, useState } from 'react'
import * as PreenchimentoHttp from '../../../../services/api/https/checklist-operacional/preenchimento.http'
import { errorHandler } from '../../../../services/util/error.service'
import { PreenchimentoStatus } from '../../../../constants/checklist-operacional'
import {
  listarPendencias,
  montarPayloadRespostas,
  parseRespostas,
  respostasVazias,
} from '../shared/respostas.util'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  GET: 'Carregando preenchimento',
  SAVE: 'Salvando respostas',
  FINALIZAR: 'Finalizando preenchimento',
}

export const MODOS = {
  PREENCHER: 'preencher',
  RESULTADO: 'resultado',
}

const INITIAL_STATE = {
  preenchimento: null,
  respostas: respostasVazias(),
  snapshot: '',
  modo: MODOS.PREENCHER,
  origem: null,
  pendentes: null,
  focoPendenteId: null,
  confirmFinalizar: false,
  confirmVoltar: false,
  voltar: false,
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
  successMessage: null,
}

function criarSnapshot(respostas, estrutura) {
  return JSON.stringify(montarPayloadRespostas(respostas, estrutura))
}

function idsPendentes(estrutura, respostas) {
  return new Set(listarPendencias(estrutura, respostas).map(pendencia => pendencia.id))
}

function carregar(prev, doc, extras) {
  const respostas = parseRespostas(doc.respostas, doc.estrutura)

  return {
    ...prev,
    preenchimento: doc,
    respostas,
    snapshot: criarSnapshot(respostas, doc.estrutura),
    pendentes: null,
    focoPendenteId: null,
    loadingMessage: LOAD_MESSAGE.NONE,
    ...extras,
  }
}

export default function usePreencherChecklist() {
  const [state, setState] = useState({ ...INITIAL_STATE })
  const montado = useRef(true)

  useEffect(() => {
    return () => {
      montado.current = false
    }
  }, [])

  const localDispatch = useMemo(() => {
    const falhar = (err, extras = {}) => {
      if (!montado.current) return

      setState(prev => ({
        ...prev,
        loadingMessage: LOAD_MESSAGE.NONE,
        errorMessage: errorHandler(err),
        ...extras,
      }))
    }

    return {
      onInit(selectedTp, origem) {
        const { preenchimentoId, modo } = origem || {}

        setState({
          ...INITIAL_STATE,
          origem: preenchimentoId ? { id: preenchimentoId } : null,
          modo: modo === MODOS.RESULTADO ? MODOS.RESULTADO : MODOS.PREENCHER,
          loadingMessage: preenchimentoId ? LOAD_MESSAGE.GET : LOAD_MESSAGE.NONE,
          voltar: !preenchimentoId,
        })
      },
      async getPreenchimento(tpId, origem, modo) {
        try {
          const doc = await PreenchimentoHttp.getPreenchimento(tpId, origem.id)
          if (!montado.current) return

          const finalizado = doc.status === PreenchimentoStatus.Finalizado.label
          if (modo === MODOS.RESULTADO && !finalizado) {
            return falhar('Somente preenchimentos finalizados possuem resultado', { voltar: true })
          }
          if (doc.status === PreenchimentoStatus.Expirado.label) {
            return falhar(
              'Preenchimento expirado não pode ser preenchido. Altere o deadline para reativá-lo',
              { voltar: true },
            )
          }

          setState(prev => carregar(prev, doc, { modo: finalizado ? MODOS.RESULTADO : prev.modo }))
        } catch (err) {
          falhar(err, { voltar: true })
        }
      },
      async save(tpId, preenchimento, respostas) {
        try {
          const payload = montarPayloadRespostas(respostas, preenchimento.estrutura)
          const doc = await PreenchimentoHttp.saveRespostas(tpId, preenchimento.id, payload)
          if (!montado.current) return

          setState(prev => carregar(prev, doc, { successMessage: 'Respostas salvas com sucesso' }))
        } catch (err) {
          falhar(err)
        }
      },
      async finalizar(tpId, preenchimento, respostas) {
        try {
          const payload = montarPayloadRespostas(respostas, preenchimento.estrutura)
          const doc = await PreenchimentoHttp.finalizarPreenchimento(
            tpId,
            preenchimento.id,
            payload,
          )
          if (!montado.current) return

          setState(prev =>
            carregar(prev, doc, {
              modo: MODOS.RESULTADO,
              successMessage: 'Preenchimento finalizado com sucesso',
            }),
          )
        } catch (err) {
          falhar(err)
        }
      },
      onRespostasChange(atualizar) {
        setState(prev => {
          const respostas = atualizar(prev.respostas)
          const pendentes = prev.pendentes
            ? idsPendentes(prev.preenchimento.estrutura, respostas)
            : null

          return { ...prev, respostas, pendentes }
        })
      },
      onBtnSalvarClicked() {
        setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.SAVE }))
      },
      onBtnFinalizarClicked() {
        setState(prev => {
          const pendencias = listarPendencias(prev.preenchimento.estrutura, prev.respostas)
          if (!pendencias.length) return { ...prev, confirmFinalizar: true }

          const descricao =
            pendencias.length === 1
              ? '1 campo sem resposta'
              : `${pendencias.length} campos sem resposta`

          return {
            ...prev,
            pendentes: new Set(pendencias.map(pendencia => pendencia.id)),
            focoPendenteId: pendencias[0].id,
            errorMessage: `Não é possível finalizar: ${descricao}. Primeiro: ${pendencias[0].path}`,
          }
        })
      },
      onConfirmFinalizar(confirmado) {
        setState(prev => ({
          ...prev,
          confirmFinalizar: false,
          loadingMessage: confirmado ? LOAD_MESSAGE.FINALIZAR : prev.loadingMessage,
        }))
      },
      onBtnVoltarClicked() {
        setState(prev => {
          const alterado =
            prev.modo === MODOS.PREENCHER &&
            !!prev.preenchimento &&
            criarSnapshot(prev.respostas, prev.preenchimento.estrutura) !== prev.snapshot

          return alterado ? { ...prev, confirmVoltar: true } : { ...prev, voltar: true }
        })
      },
      onConfirmVoltar(confirmado) {
        setState(prev => ({ ...prev, confirmVoltar: false, voltar: confirmado }))
      },
      clearMessages() {
        setState(prev => ({
          ...prev,
          errorMessage: null,
          successMessage: null,
          focoPendenteId: null,
        }))
      },
    }
  }, [])

  return { state, localDispatch }
}
